import { useEffect, useState } from 'react'

/**
 * PriceSlider — ONE dual-handle range control. Two overlapping
 * range inputs share a track; the segment between the thumbs is
 * painted forest so the selected window reads visually as one
 * range. The thumbs can't cross — onChange clamps min less than or
 * equal to max.
 *
 * Reports the new tuple back to the parent on commit (mouseup /
 * touchend / keyboard change) so the list refetch doesn't fire on
 * every drag pixel.
 *
 * Pure presentational — the parent owns the canonical filter state.
 */
interface Props {
  readonly catalogMinPaisa: number
  readonly catalogMaxPaisa: number
  readonly valueMinPaisa: number
  readonly valueMaxPaisa: number
  readonly onCommit: (next: { minPaisa: number; maxPaisa: number }) => void
  readonly disabled?: boolean
}

function rupeesLabel(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

export function PriceSlider({
  catalogMinPaisa,
  catalogMaxPaisa,
  valueMinPaisa,
  valueMaxPaisa,
  onCommit,
  disabled = false
}: Props): JSX.Element {
  const [min, setMin] = useState<number>(valueMinPaisa)
  const [max, setMax] = useState<number>(valueMaxPaisa)

  // Re-sync when the parent's canonical filter changes (e.g. Clear all,
  // or the category-driven bounds shift the range entirely).
  useEffect(() => { setMin(valueMinPaisa) }, [valueMinPaisa])
  useEffect(() => { setMax(valueMaxPaisa) }, [valueMaxPaisa])

  const span = Math.max(1, catalogMaxPaisa - catalogMinPaisa)
  const step = Math.max(1, Math.floor(span / 100))
  // Percent positions for the painted gap between the thumbs.
  const leftPct = Math.min(100, Math.max(0, ((min - catalogMinPaisa) / span) * 100))
  const rightPct = Math.min(100, Math.max(0, ((max - catalogMinPaisa) / span) * 100))

  function handleMinChange(raw: number): void {
    setMin(Math.min(raw, max))
  }
  function handleMaxChange(raw: number): void {
    setMax(Math.max(raw, min))
  }
  function commit(): void {
    onCommit({ minPaisa: min, maxPaisa: max })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between font-body text-body-xs text-text-light/70">
        <span className="font-mono text-text-light">{rupeesLabel(min)}</span>
        <span className="font-mono text-text-light">{rupeesLabel(max)}</span>
      </div>
      <div className="dual-range relative h-5">
        {/* Inactive track */}
        <div
          aria-hidden
          className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-text-light/20"
        />
        {/* Active segment between the two thumbs (forest green). */}
        <div
          aria-hidden
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-forest"
          style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
        />
        {/* Min thumb. */}
        <input
          type="range"
          min={catalogMinPaisa}
          max={catalogMaxPaisa}
          step={step}
          value={min}
          disabled={disabled}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          onMouseUp={commit}
          onTouchEnd={commit}
          onKeyUp={commit}
          aria-label="Minimum price"
          className="absolute inset-0 z-[3] w-full appearance-none bg-transparent accent-forest"
        />
        {/* Max thumb (above the min input so it stays grabbable even at the edges). */}
        <input
          type="range"
          min={catalogMinPaisa}
          max={catalogMaxPaisa}
          step={step}
          value={max}
          disabled={disabled}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          onMouseUp={commit}
          onTouchEnd={commit}
          onKeyUp={commit}
          aria-label="Maximum price"
          className="absolute inset-0 z-[4] w-full appearance-none bg-transparent accent-forest"
        />
      </div>
      <p className="font-body text-body-xs text-text-light/70">
        Range {rupeesLabel(catalogMinPaisa)} – {rupeesLabel(catalogMaxPaisa)}
      </p>
      {/* Local style: hide the native track on both inputs (we paint
          our own behind) and keep only the thumb visible + clickable. */}
      <style>{`
        .dual-range input[type='range'] { pointer-events: none; }
        .dual-range input[type='range']::-webkit-slider-runnable-track,
        .dual-range input[type='range']::-moz-range-track { background: transparent; height: 0; }
        .dual-range input[type='range']::-webkit-slider-thumb {
          pointer-events: auto;
          -webkit-appearance: none; appearance: none;
          width: 16px; height: 16px; border-radius: 9999px;
          background: #4F6F52; border: 2px solid #FAF5ED;
          box-shadow: 0 1px 2px rgba(0,0,0,0.15);
          cursor: pointer;
        }
        .dual-range input[type='range']::-moz-range-thumb {
          pointer-events: auto;
          width: 16px; height: 16px; border-radius: 9999px;
          background: #4F6F52; border: 2px solid #FAF5ED;
          box-shadow: 0 1px 2px rgba(0,0,0,0.15);
          cursor: pointer;
        }
      `}</style>
    </div>
  )
}
